import React, { Dispatch, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Car } from "../../../../services";
import { CarSubscribeTypeEnum } from "../../../../services/Car";
import useModelSubscribe from "../../../../hooks/useModelSubscribe2";
import { useExecutorsSubHandler } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";

/**
 * Мультивыбор автомобилей по парковому номеру; рядом с номером — ФИО
 * водителей экипажа (из подписки на исполнителей тех же филиалов).
 * Список ограничивается филиалами из `taxiServiceIds`.
 */
const MultiSelectCarWithModalBase: React.FC<
	MultiSelectCarWithModalBase.Props
> = ({
	value,
	onChange,
	disabled,
	titleText,
	required,
	modalSetting,
	sort,
	taxiServiceIds,
	...props
}) => {
	const { t } = useTranslation();

	// Без `order`: с ним подписка car.subscribe не присылает событий (список пуст);
	// сортировка — на клиенте в MultiSelectWithModal. Тип `car_modal`: санитайзер
	// сервера отдаёт callSign (парковый номер) только для car_page и car_modal.
	const subscribeOptions = useMemo<Car.SubscribeOptions>(
		() => ({
			subscribeType: CarSubscribeTypeEnum.CAR_MODAL,
			...(taxiServiceIds?.length ? { taxiServiceIds } : {}),
		}),
		[taxiServiceIds],
	);

	const subscribe = useModelSubscribe(subscribeOptions, Car);

	const items = useMemo<Car.Model[]>(
		() => subscribe.models ?? [],
		[subscribe.models],
	);

	const { getAllExecutorsSubHandler } = useExecutorsSubHandler();
	const executorOptions = useMemo(
		() => ({
			...(taxiServiceIds?.length ? { taxiServiceIds } : {}),
			order: { callSign: "ASC" as const },
		}),
		[taxiServiceIds],
	);
	const executors = getAllExecutorsSubHandler(executorOptions);

	/** carId → ФИО водителей, у которых это авто в экипаже. */
	const driversByCarId = useMemo(() => {
		const map = new Map<number, string[]>();
		executors.forEach((executor) => {
			const { person } = executor;
			const name =
				[person?.lastName, person?.firstName, person?.fatherName]
					.filter(Boolean)
					.join(" ") ||
				executor.alias ||
				"";
			if (!name) return;
			executor.cars?.forEach((car) => {
				if (!car?.id) return;
				map.set(car.id, [...(map.get(car.id) ?? []), name]);
			});
		});
		return map;
	}, [executors]);

	const [error, setError] = useState("");

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			items.map((item) => {
				const number = item.parkNumber || item.registrationNumber;
				const drivers = driversByCarId.get(item.id);
				const label = drivers?.length
					? `${number} — ${drivers.join(", ")}`
					: number;
				return {
					key: item.id,
					label,
					value: item.id,
					name: label,
					data: item,
				};
			}),
		[items, driversByCarId],
	);

	const selected = useMemo<MultiSelectWithModalBase.Value>(
		() => selectOptions.filter((item) => value.includes(item.value)),
		[selectOptions, value],
	);

	const selectOnChange = useCallback(
		(newValue: MultiSelectWithModalBase.Value) => {
			setError("");
			onChange(newValue.map((item) => item.value as number));
		},
		[onChange],
	);

	const title = useMemo(() => t(titleText), [t, titleText]);

	return (
		<MultiSelectWithModal
			error={error}
			disabled={disabled}
			value={selected}
			title={title}
			options={selectOptions}
			onChange={selectOnChange}
			required={required}
			pullUpItemInsideArray
			hiddenButton={false}
			showSelectAll
			modalSetting={modalSetting}
			sort={{
				show: true,
				active: true,
				...(sort || {}),
			}}
			{...props}
		/>
	);
};

export const MultiSelectCarWithModal = memo(MultiSelectCarWithModalBase);

declare namespace MultiSelectCarWithModalBase {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		/** Ограничить список авто филиалами. */
		taxiServiceIds?: number[];
	}

	type Value = number[];
}

export default MultiSelectCarWithModalBase;
